import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AppButton } from '@/shared/ui/button'

import { useUnit } from 'effector-react'

import { lang } from '@/shared/constants/lang'
import { useStyles } from './style'

import { goToAuth, goToShop } from '@/app/routing/events'
import { $userLanguage, $userToken } from '@/entities/user'
import { RewardRarity } from '@/entities/cases'

import { getCaseListFx, $casesList, $isOpenedFree, $lastRewardFromCase, $lastViewedCaseId } from '@/entities/cases'
import { useHideNav } from '@/shared/hooks/useHideNav'
import { CaseIdToFolder } from '@/pages/casePage/types'
import { Icon, TIconVariant } from '@/shared/ui/icon'
import Ringgy from '@/features/ringgy'
import { useOpenCase } from '@/shared/hooks/useOpenCase'
import { formatNumber } from '@/shared/util/formatNumber'
import { RewardKindToCurrency, rewardRarityObj, RewardType, SingleDropRewardNameToText } from './types'
import { Case } from '@/entities/cases'
import { getUniqueGiftKey } from './constants'
// import { mockSubscriptionReward, mockUniqueReward } from '@/shared/constants/mock'
import { convertMonts } from '@/shared/util/convertMonths'
import { Spiner } from '@/shared/ui/spiner'
export type UniqueGiftTexts = {
  [key in keyof typeof CaseIdToFolder]: {
    heading: string
    description: string
  }
}

export const RewardClaim = () => {
  useHideNav()
  const userLang = useUnit($userLanguage)
  const userToken = useUnit($userToken)

  const { classes } = useStyles()

  const lastViewedCaseId = useUnit($lastViewedCaseId)
  const internalCaseId = lastViewedCaseId ? lastViewedCaseId : 1

  const lb = useUnit($casesList)

  useEffect(() => {
    if (!lb && userToken) getCaseListFx({ token: userToken })
  }, [lb, userToken])

  const lbState: Case | undefined = lb?.find((_case: Case) => _case.id === internalCaseId)

  const folder = lbState?.name?.replace(/\s/g, '').toLowerCase()
  const lastOpenedReward = useUnit($lastRewardFromCase)
  const reward = lastOpenedReward.reward
  // const reward = mockSubscriptionReward
  // const reward = mockUniqueReward
  const isUniqueGift = reward && reward?.kind === 0

  const [isVideoEnded, setVideoEnded] = useState(false)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [playbackRate, setPlaybackRate] = useState<number>(1)

  const isOpenedFree = useUnit($isOpenedFree)

  const { handleOpenCase } = useOpenCase()
  const btnTexts = lang[userLang].pages.btnTexts

  const imgSrc = `/assets/lootBoxRelated/rewardsPreview/${folder}/${reward?.name}.png`

  useEffect(() => {
    const video = videoRef.current
    if (video) {
      video.playbackRate = playbackRate
    }
  }, [playbackRate])

  const handleTapRing = useCallback((tapCount: number) => {
    if (tapCount <= 1) {
      setPlaybackRate(1)
    } else if (tapCount <= 3) {
      setPlaybackRate(2)
    } else if (tapCount <= 5) {
      setPlaybackRate(3)
    } else {
      setPlaybackRate(5)
    }
  }, [])

  useEffect(() => {
    const video = videoRef.current
    if (video) {
      video.addEventListener('ended', () => setVideoEnded(true))
    }

    return () => {
      if (video) {
        video.removeEventListener('ended', () => setVideoEnded(true))
      }
    }
  }, [])

  if (!lb) return <Spiner size="m" />

  return (
    <>
      <>
        <Ringgy isEnabled={!isVideoEnded} onTap={handleTapRing} />
        <MemoizedVideo boxId={internalCaseId!} videoRef={videoRef} />
      </>

      <div
        className={classes.root}
        data-isvideoended={isVideoEnded}
        data-rarity={rewardRarityObj[reward!.rarity as keyof typeof rewardRarityObj][0]}
        data-isps5={CaseIdToFolder.ps5 === lastViewedCaseId && isUniqueGift}
      >
        <div className={classes.headingWrapper}>
          <span className={classes.heading}>{lang[userLang].pages.boxPage.widgetTexts.rewardClaim.heading}</span>
        </div>
        <div className={classes.rewardInfoWrapper} data-isunique={isUniqueGift}>
          <div className={classes.imageWrapper} data-isunique={isUniqueGift}>
            <img src={imgSrc} alt="" className={classes.img} />
          </div>
          <RenderRewardContent reward={reward!} isUniqueReward={isUniqueGift!} />
        </div>
        <div className={classes.buttonsWrapper}>
          <AppButton
            data-free={isOpenedFree}
            onClick={() => {
              if (userToken) {
                getCaseListFx({ token: userToken })
                goToShop({ tab: 1 })
              } else {
                goToAuth()
              }
            }}
            variant="primary"
            className={classes.claimButton}
          >
            {lang[userLang].pages.btnTexts.claim}
          </AppButton>
          {!isOpenedFree && (
            <AppButton className={classes.shareButton} onClick={handleOpenCase}>
              <Icon view="moreBoxes" style={{ opacity: 0.7 }} />
              {btnTexts.openMore}
            </AppButton>
          )}
        </div>
      </div>
    </>
  )
}

type RenderRewardValueProps = {
  isUniqueReward: boolean
  reward: RewardType
}

export const RenderRewardContent: React.FC<RenderRewardValueProps> = (props) => {
  const { isUniqueReward, reward } = props
  const userLang = useUnit($userLanguage)
  const lastViewedCaseId = useUnit($lastViewedCaseId)
  const { classes } = useStyles()
  const rewardCurrency = RewardKindToCurrency[reward.kind as number]
  const rewardDataAttribute = reward?.rarity ? RewardRarity[isUniqueReward ? 5 : reward.rarity] : ''
  const uniqueGiftRarityText = userLang === 'en' ? rewardRarityObj[5][0] : rewardRarityObj[5][1]

  const _rarityText = useMemo(() => {
    if (userLang && reward?.rarity) {
      return userLang === 'en'
        ? rewardRarityObj[reward.rarity as keyof typeof rewardRarityObj][0]
        : rewardRarityObj[reward.rarity as keyof typeof rewardRarityObj][1]
    }
    return 'Common'
  }, [userLang, reward?.rarity])

  const rewardRarityText = reward ? (isUniqueReward ? uniqueGiftRarityText : _rarityText) : ''
  const units = lang[userLang].pages.units
  const texts = lang[userLang].pages.boxPage.widgetTexts.rewardClaim

  const IS_TG_STARS_REWARD = rewardCurrency === 'tgStar'
  const IS_SUBSCRIPTION_REWARD = reward.kind === 3
  const IS_YEARS = Number(reward.name) >= 12

  const uniqueGiftTexts = lang[userLang].pages.boxPage.widgetTexts.rewardClaim.uniqueGifts as Record<
    keyof typeof CaseIdToFolder,
    { heading: string; description: string }
  >

  const giftKey = lastViewedCaseId ? getUniqueGiftKey(lastViewedCaseId) : undefined
  return (
    <div className={classes.textWrapper}>
      <div className={classes.amoutOfPointsWrapper}>
        {isUniqueReward && giftKey ? (
          <>
            <span className={classes.uniqueGiftText}>
              {uniqueGiftTexts[giftKey as keyof typeof uniqueGiftTexts].heading}
            </span>
          </>
        ) : (
          <>
            <span className={classes.amountOfPoints}>
              {IS_TG_STARS_REWARD && texts.giftWorth} {''}
              {IS_SUBSCRIPTION_REWARD ? (
                <>
                  {convertMonts(reward.name)} {IS_YEARS ? units.year : units.months}
                </>
              ) : reward.kind === 0 ? (
                SingleDropRewardNameToText[reward.name as keyof typeof SingleDropRewardNameToText]
              ) : (
                formatNumber(reward.name)
              )}
            </span>
            {!IS_SUBSCRIPTION_REWARD && reward.kind !== 0 && <Icon view={rewardCurrency as TIconVariant} width={32} />}
          </>
        )}
      </div>
      {reward.kind < 2 && (
        <span className={classes.rarityText} data-textcolor={rewardDataAttribute}>
          {rewardRarityText}
        </span>
      )}

      {isUniqueReward && giftKey && (
        <p className={classes.congrats}>{uniqueGiftTexts[giftKey as keyof typeof uniqueGiftTexts].description}</p>
      )}
    </div>
  )
}

const MemoizedVideo = React.memo<{
  videoRef: React.RefObject<HTMLVideoElement>
  boxId: number
}>(({ videoRef, boxId }) => {
  const { classes } = useStyles()
  const videoAsset = CaseIdToFolder[boxId]

  const commonVideoProps = {
    ref: videoRef,
    src: `/assets/video/${videoAsset}_roulette.webm`,
    autoPlay: true,
    muted: true,
    playsInline: true,
  }

  const [appeared, setAppeared] = useState<boolean>(false)

  useEffect(() => {
    setTimeout(() => {
      setAppeared(true)
    }, 322)
  }, [videoRef, boxId])

  return (
    <div className={classes.videoWrapper}>
      <video data-appeared={appeared} {...commonVideoProps} controls={false} />
    </div>
  )
})
